import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useTranslation } from "react-i18next";
import {
  User,
  CheckCircle2,
  Eye,
  Trash2,
  Users,
  Crown,
  Settings,
  Building2,
  Shield,
  ShoppingBag,
  Briefcase,
  UserCog,
  Star,
  Hash,
} from "lucide-react";

const getRoleIcon = (role) => {
  switch(role) {
    case 'platform_admin': return <Crown className="w-5 h-5 text-yellow-400" />;
    case 'operations_manager': return <Settings className="w-5 h-5 text-purple-400" />;
    case 'client_admin': return <Building2 className="w-5 h-5 text-blue-400" />;
    case 'client_viewer': return <Eye className="w-5 h-5 text-blue-300" />;
    case 'team_leader': return <Shield className="w-5 h-5 text-green-400" />;
    case 'worker': return <User className="w-5 h-5 text-teal-400" />;
    case 'webshop_admin': return <ShoppingBag className="w-5 h-5 text-orange-400" />;
    case 'rentals_admin': return <Briefcase className="w-5 h-5 text-amber-400" />;
    default: return <User className="w-5 h-5 text-gray-400" />;
  }
};

const getRoleColor = (role) => {
  switch(role) {
    case 'platform_admin': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
    case 'operations_manager': return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
    case 'client_admin': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
    case 'client_viewer': return 'bg-blue-400/20 text-blue-300 border-blue-400/30';
    case 'team_leader': return 'bg-green-500/20 text-green-400 border-green-500/30';
    case 'worker': return 'bg-teal-500/20 text-teal-400 border-teal-500/30';
    case 'webshop_admin': return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
    case 'rentals_admin': return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
    default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  }
};

const AVAILABLE_ROLES = [
  { value: 'platform_admin', label: 'Platform Admin', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50 hover:bg-yellow-500/30' },
  { value: 'operations_manager', label: 'Operations Manager', color: 'bg-purple-500/20 text-purple-400 border-purple-500/50 hover:bg-purple-500/30' },
  { value: 'client_admin', label: 'Client Admin', color: 'bg-blue-500/20 text-blue-400 border-blue-500/50 hover:bg-blue-500/30' },
  { value: 'client_viewer', label: 'Client Viewer', color: 'bg-blue-400/20 text-blue-300 border-blue-400/50 hover:bg-blue-400/30' },
  { value: 'team_leader', label: 'Team Leader', color: 'bg-green-500/20 text-green-400 border-green-500/50 hover:bg-green-500/30' },
  { value: 'worker', label: 'Worker', color: 'bg-teal-500/20 text-teal-400 border-teal-500/50 hover:bg-teal-500/30' },
  { value: 'webshop_admin', label: 'Webshop Admin', color: 'bg-orange-500/20 text-orange-400 border-orange-500/50 hover:bg-orange-500/30' },
  { value: 'rentals_admin', label: 'Rentals Admin', color: 'bg-amber-500/20 text-amber-400 border-amber-500/50 hover:bg-amber-500/30' },
];

export const AllUsersTab = ({ 
  allUsers = [], 
  searchQuery, 
  setSearchQuery,
  onChangeRole, 
  onViewDetails, 
  onDelete,
  onImpersonate,
  currentUserId
}) => {
  const { t } = useTranslation();
  // Ensure allUsers is always an array
  const usersArray = Array.isArray(allUsers) ? allUsers : [];
  
  // Get unique roles that have users
  const rolesWithUsers = AVAILABLE_ROLES.filter(
    role => usersArray.some(u => u.role === role.value)
  );

  const filteredUsers = usersArray.filter(u => 
    !searchQuery || 
    u.role === searchQuery || 
    u.name?.toLowerCase().includes(searchQuery.toLowerCase()) || 
    u.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Card className="bg-[#0A0A0A] border-white/10">
      <CardContent className="p-4">
        <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg mb-4">
          <p className="text-blue-400 text-sm">
            <Users className="w-4 h-4 inline mr-2" />
            {t('adminWorkers.fullControl')}
          </p>
        </div>
        
        {/* Role filter for all users - Colored buttons */}
        <div className="flex flex-wrap gap-2 mb-4">
          <Button
            size="sm"
            variant="outline"
            className={`border-[#23F7DD]/50 ${!searchQuery ? 'bg-[#23F7DD] text-black' : 'text-[#23F7DD] hover:bg-[#23F7DD]/20'}`}
            onClick={() => setSearchQuery("")}
          >
            {t('common.all')} ({allUsers.length})
          </Button>
          {rolesWithUsers.map(role => {
            const count = allUsers.filter(u => u.role === role.value).length;
            const isSelected = searchQuery === role.value;
            return (
              <Button
                key={role.value}
                size="sm"
                variant="outline"
                className={`border ${isSelected ? role.color.replace('/20', '/40') : role.color}`}
                onClick={() => setSearchQuery(role.value)}
              >
                {role.label.replace('_', ' ')} ({count})
              </Button>
            );
          })}
        </div>
        
        <div className="space-y-2">
          {filteredUsers.map((user) => (
            <div key={user.user_id} className="p-4 bg-white/5 rounded-lg border border-white/10 hover:border-blue-500/30 transition-colors">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${getRoleColor(user.role).split(' ')[0]}`}>
                    {getRoleIcon(user.role)}
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-white font-medium">{user.name}</p>
                      <span className={`px-2 py-0.5 text-xs rounded border ${getRoleColor(user.role)}`}>
                        {user.role?.replace('_', ' ')}
                      </span>
                      {user.is_approved && (
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                      )}
                      
                      {/* Worker/TL Code Badge - only for workers and team leaders */}
                      {(user.role === 'worker' || user.role === 'team_leader') && (user.worker_code || user.team_leader_code) && (
                        <span className="px-2 py-0.5 text-xs font-mono rounded bg-[#23F7DD]/20 text-[#23F7DD] border border-[#23F7DD]/30 flex items-center gap-1">
                          <Hash className="w-3 h-3" />
                          {user.team_leader_code || user.worker_code}
                        </span>
                      )}
                      
                      {/* Rating - only for workers and team leaders */}
                      {(user.role === 'worker' || user.role === 'team_leader') && user.rating > 0 && (
                        <span className="px-2 py-0.5 text-xs rounded bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 flex items-center gap-1">
                          <Star className="w-3 h-3 fill-yellow-400" />
                          {user.rating.toFixed(1)}
                          {user.total_ratings > 0 && (
                            <span className="text-yellow-400/60">({user.total_ratings})</span>
                          )}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-[#94A3B8]">{user.email}</p>
                    {user.company_id && (
                      <p className="text-xs text-[#64748B]">{t('adminWorkers.company')}: {user.company_id}</p>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {/* Login As button - only show for other users */}
                  {onImpersonate && user.user_id !== currentUserId && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onImpersonate(user.user_id)}
                      className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
                      title={`${t('adminWorkers.loginAs')} ${user.name}`}
                      data-testid={`impersonate-${user.user_id}`}
                    >
                      <UserCog className="w-4 h-4 mr-1" />
                      {t('adminWorkers.loginAs')}
                    </Button>
                  )}
                  
                  {/* Role change dropdown */}
                  <select
                    value={user.role}
                    onChange={(e) => onChangeRole(user.user_id, e.target.value)}
                    className="bg-black/50 border border-white/20 text-white text-sm rounded px-2 py-1"
                  >
                    {AVAILABLE_ROLES.map(role => (
                      <option key={role.value} value={role.value}>
                        {role.label}
                      </option>
                    ))}
                  </select>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onViewDetails(user)}
                    className="border-white/20 text-white hover:bg-white/5"
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onDelete(user)}
                    className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
